import configService from "./configService";
import posthogService from "./posthogService";

// HTTP Method types
export type HttpMethod = "GET" | "HEAD" | "POST" | "PUT" | "DELETE" | "PATCH";
export type HttpRequestScope =
  | "account"
  | "actor"
  | "account-transition"
  | "session-transition";
type ResolvedHttpRequestScope = HttpRequestScope | "public";

export class AccountContextRequestInvalidatedError extends Error {
  constructor(message = "Request invalidated by account context change") {
    super(message);
    this.name = "AccountContextRequestInvalidatedError";
    Object.setPrototypeOf(this, AccountContextRequestInvalidatedError.prototype);
  }
}

// Request configuration interface
export interface HttpRequestConfig {
  method: HttpMethod;
  url: string;
  data?: any;
  headers?: Record<string, string>;
  requiresAuth?: boolean;
  /** Include an existing Bearer token on a public request without requiring a signed-in user. */
  includeAuthIfAvailable?: boolean;
  credentials?: RequestCredentials;
  keepalive?: boolean;

  timeout?: number;
  /** Disable replay for credentials whose ownership must be checked by the caller. */
  retryOnUnauthorized?: boolean;
  cache?: RequestCache;
  baseUrlType?: "api" | "auth";
  signal?: AbortSignal;
  /** Defaults to actor for auth/unauthenticated calls and account otherwise. */
  requestScope?: HttpRequestScope;
}

// Response interface
export interface HttpResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
}

/** Result of `openEventStream`: the caller owns the reader and must call `detach()` once when done. */
export interface EventStreamConnection {
  response: Response;
  reader: ReadableStreamDefaultReader<Uint8Array>;
  detach: () => void;
}

// Error response interface
export interface HttpError extends Error {
  status?: number;
  response?: {
    data?: any;
    status: number;
    statusText: string;
    headers?: Headers;
  };
}

export interface AuthTokenPair {
  accessToken: string;
  refreshToken: string;
}

interface AuthTokenSnapshot {
  accessToken: string | null;
  refreshToken: string | null;
  version: number;
}

type StoredAuthTokenPair = Pick<
  AuthTokenSnapshot,
  "accessToken" | "refreshToken"
>;

interface SuccessfulTokenRefresh {
  outcome: "refreshed";
  source: AuthTokenSnapshot;
  installed: AuthTokenSnapshot;
}

type TokenRefreshResult =
  | SuccessfulTokenRefresh
  | {
      outcome: "failed" | "invalidated";
      source: AuthTokenSnapshot;
    };

interface RequestLifecycleContext {
  scope: ResolvedHttpRequestScope;
  generation: number | null;
  controller: AbortController | null;
}

interface RequestAbortState {
  signal: AbortSignal;
  clearTimeout: () => void;
  cleanup: () => void;
}

// HTTP Service class
class HttpService {
  private static instance: HttpService;
  private baseUrl: string;
  private authBaseUrl: string;
  private defaultTimeout: number = 30000; // 30 seconds
  private authTokenKey: string = "plutus_auth_access_token";
  private refreshTokenKey: string = "plutus_auth_refresh_token";
  private authTokenVersion = 0;
  private observedAuthTokenPair: StoredAuthTokenPair;
  private accountContextAccessDeniedHandler: (() => void) | null = null;
  private accountContextGeneration = 0;
  private accountContextTransitionDepth = 0;
  private activeAccountRequestControllers = new Set<AbortController>();
  private actorSessionListeners = new Set<() => void>();
  private actorSessionGeneration = 0;
  private actorSessionTransitionDepth = 0;
  private activeActorRequestControllers = new Set<AbortController>();

  // Track if we're currently refreshing to prevent multiple simultaneous refreshes
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<TokenRefreshResult> | null = null;
  private activeRefreshController: AbortController | null = null;
  private lastSuccessfulRefresh: SuccessfulTokenRefresh | null = null;

  private constructor() {
    this.baseUrl = configService.getApiBaseUrl();
    this.authBaseUrl = configService.getAuthApiBaseUrl();
    this.observedAuthTokenPair = this.getStoredAuthTokenPair();
  }

  /**
   * Get the singleton instance of HttpService
   */
  public static getInstance(): HttpService {
    if (!HttpService.instance) {
      HttpService.instance = new HttpService();
    }
    return HttpService.instance;
  }

  /**
   * Get the authentication token from localStorage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem(this.authTokenKey);
  }

  /**
   * Get the refresh token from localStorage
   */
  private getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  public getAuthTokenPair(): AuthTokenPair | null {
    const accessToken = this.getAuthToken();
    const refreshToken = this.getRefreshToken();
    return accessToken && refreshToken ? { accessToken, refreshToken } : null;
  }

  private getStoredAuthTokenPair(): StoredAuthTokenPair {
    return {
      accessToken: this.getAuthToken(),
      refreshToken: this.getRefreshToken(),
    };
  }

  private adoptPersistedAuthTokenPair(): void {
    this.observedAuthTokenPair = this.getStoredAuthTokenPair();
  }

  private authTokenPairValuesMatch(
    first: StoredAuthTokenPair,
    second: StoredAuthTokenPair
  ): boolean {
    return first.accessToken === second.accessToken
      && first.refreshToken === second.refreshToken;
  }

  private persistedAuthTokensMatchObserved(): boolean {
    return this.authTokenPairValuesMatch(
      this.getStoredAuthTokenPair(),
      this.observedAuthTokenPair
    );
  }

  /**
   * Reports a browser-wide token write that this tab has not adopted yet.
   * AuthContext uses this at non-HTTP await boundaries so a cross-tab switch
   * cannot publish or roll back account state before its storage event runs.
   */
  public hasUnadoptedExternalAuthTokenChange(): boolean {
    return !this.persistedAuthTokensMatchObserved();
  }

  private getAuthTokenSnapshot(): AuthTokenSnapshot {
    return {
      accessToken: this.getAuthToken(),
      refreshToken: this.getRefreshToken(),
      version: this.authTokenVersion,
    };
  }

  private authTokenSnapshotsMatch(
    first: AuthTokenSnapshot,
    second: AuthTokenSnapshot
  ): boolean {
    return first.version === second.version
      && this.authTokenPairValuesMatch(first, second);
  }

  /**
   * Replaces the browser-wide token pair as one synchronous transaction. If
   * either storage write fails, the previous pair is restored before the error
   * is surfaced. localStorage is shared by tabs, so this intentionally changes
   * the active account context for the whole browser profile.
   */
  public replaceAuthTokenPair(tokens: AuthTokenPair): void {
    if (!tokens.accessToken || !tokens.refreshToken) {
      throw new Error("Both access and refresh tokens are required");
    }

    const previousAccessToken = this.getAuthToken();
    const previousRefreshToken = this.getRefreshToken();
    try {
      localStorage.setItem(this.authTokenKey, tokens.accessToken);
      localStorage.setItem(this.refreshTokenKey, tokens.refreshToken);
    } catch (error) {
      this.restoreStoredToken(this.authTokenKey, previousAccessToken);
      this.restoreStoredToken(this.refreshTokenKey, previousRefreshToken);
      throw error;
    }
    this.observedAuthTokenPair = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
    };
    this.authTokenVersion += 1;
    this.lastSuccessfulRefresh = null;
  }

  public updateAuthTokens(accessToken: string, refreshToken?: string): void {
    const source = this.getAuthTokenSnapshot();
    if (!this.authTokenPairValuesMatch(source, this.observedAuthTokenPair)) {
      throw new AccountContextRequestInvalidatedError(
        "Token refresh invalidated before external auth state was adopted"
      );
    }
    const existingRefreshToken = refreshToken || source.refreshToken;
    if (!existingRefreshToken) {
      throw new Error("Refresh token not found");
    }
    this.replaceAuthTokenPair({ accessToken, refreshToken: existingRefreshToken });
    const installed = this.getAuthTokenSnapshot();
    if (
      source.accessToken
      && source.refreshToken
      && installed.accessToken === accessToken
      && installed.refreshToken === existingRefreshToken
    ) {
      this.lastSuccessfulRefresh = {
        outcome: "refreshed",
        source,
        installed,
      };
    }
  }

  public clearAuthTokenPair(): void {
    localStorage.removeItem(this.authTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    this.observedAuthTokenPair = {
      accessToken: null,
      refreshToken: null,
    };
    this.authTokenVersion += 1;
    this.lastSuccessfulRefresh = null;
    this.notifyActorSessionListeners();
  }

  /**
   * Adopts the pair written by another tab and invalidates older token work.
   * The storage-event caller must begin an account transition first so normal
   * account traffic stays blocked until effective-account reconciliation ends.
   */
  public notifyExternalAuthTokenChange(): void {
    this.adoptPersistedAuthTokenPair();
    this.authTokenVersion += 1;
    this.lastSuccessfulRefresh = null;
  }

  public setAccountContextAccessDeniedHandler(handler: (() => void) | null): void {
    this.accountContextAccessDeniedHandler = handler;
  }

  /**
   * Invalidates work that belongs to the previous effective account before an
   * account switch starts. Calls are balanced so overlapping transition flows
   * cannot unblock ordinary account requests before the newest flow finishes.
   */
  public beginAccountContextTransition(): void {
    this.accountContextGeneration += 1;
    this.accountContextTransitionDepth += 1;

    // A refresh started for the previous account must not install refreshed
    // credentials while account activation is in flight.
    this.authTokenVersion += 1;
    this.lastSuccessfulRefresh = null;

    for (const controller of this.activeAccountRequestControllers) {
      controller.abort();
    }
    this.activeAccountRequestControllers.clear();
    this.activeRefreshController?.abort();
  }

  public finishAccountContextTransition(): void {
    this.accountContextTransitionDepth = Math.max(
      0,
      this.accountContextTransitionDepth - 1
    );
  }

  /** A credential-free snapshot for session-owned integrations. */
  public getActorSessionState(): { generation: number; transitioning: boolean } {
    return { generation: this.actorSessionGeneration, transitioning: this.actorSessionTransitionDepth > 0 };
  }

  public subscribeActorSession(listener: () => void): () => void {
    this.actorSessionListeners.add(listener);
    return () => { this.actorSessionListeners.delete(listener); };
  }

  private notifyActorSessionListeners(): void {
    for (const listener of this.actorSessionListeners) {
      listener();
    }
  }

  /**
   * Invalidates authenticated actor work while a browser-wide session pair is
   * being reconciled. This is separate from effective-account transitions so
   * actor-owned APIs remain available during an ordinary local account switch.
   */
  public beginAuthSessionTransition(): void {
    this.actorSessionGeneration += 1;
    this.actorSessionTransitionDepth += 1;
    this.authTokenVersion += 1;
    this.lastSuccessfulRefresh = null;

    for (const controller of this.activeActorRequestControllers) {
      controller.abort();
    }
    this.activeActorRequestControllers.clear();
    this.activeRefreshController?.abort();
    this.notifyActorSessionListeners();
  }

  public finishAuthSessionTransition(): void {
    this.actorSessionTransitionDepth = Math.max(
      0,
      this.actorSessionTransitionDepth - 1
    );
    this.notifyActorSessionListeners();
  }

  private restoreStoredToken(key: string, value: string | null): void {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  }

  private handleAccountContextAccessDenied(
    error: HttpError,
    config: HttpRequestConfig,
    lifecycle: RequestLifecycleContext
  ): void {
    const isActivationRequest = /(^|\/)v3\/accounts\/[^/]+\/activate$/.test(config.url);
    if (
      lifecycle.scope !== "account"
      || !this.isRequestLifecycleCurrent(lifecycle)
      || error.status !== 403
      || error.message !== "Account context access denied"
      || isActivationRequest
      || !this.accountContextAccessDeniedHandler
    ) return;

    const handler = this.accountContextAccessDeniedHandler;
    Promise.resolve().then(() => {
      if (this.isRequestLifecycleCurrent(lifecycle)) handler();
    });
  }

  private resolveRequestScope(config: HttpRequestConfig): ResolvedHttpRequestScope {
    if (config.requestScope) return config.requestScope;
    if (config.requiresAuth === false) return "public";
    if (config.baseUrlType === "auth") return "actor";
    return "account";
  }

  private isAccountBoundScope(scope: ResolvedHttpRequestScope): boolean {
    return scope === "account" || scope === "account-transition";
  }

  private isActorBoundScope(scope: ResolvedHttpRequestScope): boolean {
    return scope === "actor" || scope === "session-transition";
  }

  private isAuthenticatedScope(scope: ResolvedHttpRequestScope): boolean {
    return scope !== "public";
  }

  private createRequestLifecycle(
    config: HttpRequestConfig
  ): RequestLifecycleContext {
    const scope = this.resolveRequestScope(config);
    if (this.isAuthenticatedScope(scope) && !this.persistedAuthTokensMatchObserved()) {
      throw new AccountContextRequestInvalidatedError(
        "Authenticated request blocked until external auth state is adopted"
      );
    }
    if (scope === "account" && this.accountContextTransitionDepth > 0) {
      throw new AccountContextRequestInvalidatedError(
        "Account-scoped request blocked during account context transition"
      );
    }
    if (
      this.actorSessionTransitionDepth > 0
      && this.isAuthenticatedScope(scope)
      && scope !== "session-transition"
    ) {
      throw new AccountContextRequestInvalidatedError(
        "Authenticated request blocked during auth session transition"
      );
    }

    if (!this.isAuthenticatedScope(scope)) {
      return { scope, generation: null, controller: null };
    }

    const controller = new AbortController();
    const generation = this.isAccountBoundScope(scope)
      ? this.accountContextGeneration
      : this.actorSessionGeneration;
    if (this.isAccountBoundScope(scope)) {
      this.activeAccountRequestControllers.add(controller);
    } else {
      this.activeActorRequestControllers.add(controller);
    }
    return {
      scope,
      generation,
      controller,
    };
  }

  private releaseRequestLifecycle(lifecycle: RequestLifecycleContext): void {
    if (lifecycle.controller) {
      if (this.isAccountBoundScope(lifecycle.scope)) {
        this.activeAccountRequestControllers.delete(lifecycle.controller);
      } else if (this.isActorBoundScope(lifecycle.scope)) {
        this.activeActorRequestControllers.delete(lifecycle.controller);
      }
    }
  }

  private isRequestLifecycleCurrent(lifecycle: RequestLifecycleContext): boolean {
    if (!this.isAuthenticatedScope(lifecycle.scope)) return true;
    const currentGeneration = this.isAccountBoundScope(lifecycle.scope)
      ? this.accountContextGeneration
      : this.actorSessionGeneration;
    return lifecycle.generation === currentGeneration
      && this.persistedAuthTokensMatchObserved();
  }

  private assertRequestLifecycleCurrent(
    lifecycle: RequestLifecycleContext
  ): void {
    if (!this.isRequestLifecycleCurrent(lifecycle)) {
      throw new AccountContextRequestInvalidatedError();
    }
  }

  private awaitRequestLifecycle<T>(
    operation: Promise<T>,
    lifecycle: RequestLifecycleContext
  ): Promise<T> {
    const signal = lifecycle.controller?.signal;
    if (!signal) return operation;
    this.assertRequestLifecycleCurrent(lifecycle);

    return new Promise<T>((resolve, reject) => {
      let settled = false;
      const rejectAsInvalidated = () => {
        if (settled) return;
        settled = true;
        signal.removeEventListener("abort", rejectAsInvalidated);
        reject(new AccountContextRequestInvalidatedError());
      };

      signal.addEventListener("abort", rejectAsInvalidated, { once: true });
      if (signal.aborted) rejectAsInvalidated();

      operation.then(
        (value) => {
          if (settled) return;
          settled = true;
          signal.removeEventListener("abort", rejectAsInvalidated);
          try {
            this.assertRequestLifecycleCurrent(lifecycle);
            resolve(value);
          } catch (error) {
            reject(error);
          }
        },
        (error) => {
          if (settled) return;
          settled = true;
          signal.removeEventListener("abort", rejectAsInvalidated);
          try {
            this.assertRequestLifecycleCurrent(lifecycle);
            reject(error);
          } catch (lifecycleError) {
            reject(lifecycleError);
          }
        }
      );
    });
  }

  private createRequestAbortState(
    lifecycle: RequestLifecycleContext,
    callerSignal: AbortSignal | undefined,
    timeout: number
  ): RequestAbortState {
    const controller = new AbortController();
    const detachListeners: Array<() => void> = [];
    const upstreamSignals = new Set<AbortSignal>();
    if (lifecycle.controller) upstreamSignals.add(lifecycle.controller.signal);
    if (callerSignal) upstreamSignals.add(callerSignal);

    for (const signal of upstreamSignals) {
      const abort = () => controller.abort();
      signal.addEventListener("abort", abort, { once: true });
      detachListeners.push(() => signal.removeEventListener("abort", abort));
      if (signal.aborted) controller.abort();
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (!controller.signal.aborted) {
      timeoutId = setTimeout(() => controller.abort(), timeout);
    }

    const clearRequestTimeout = () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };

    return {
      signal: controller.signal,
      clearTimeout: clearRequestTimeout,
      cleanup: () => {
        clearRequestTimeout();
        for (const detach of detachListeners) detach();
      },
    };
  }

  /**
   * Build headers for the request
   */
  private buildHeaders(
    config: HttpRequestConfig,
    authSnapshot: AuthTokenSnapshot = this.getAuthTokenSnapshot()
  ): Record<string, string> {
    const headers: Record<string, string> = {};

    // Don't set Content-Type for FormData (browser sets with boundary) or Blob/File (caller sets explicitly)
    if (!(config.data instanceof FormData) && !(config.data instanceof Blob)) {
      headers["Content-Type"] = "application/json";
    }

    // Add custom headers (allows overriding Content-Type for Blob uploads)
    if (config.headers) {
      Object.assign(headers, config.headers);
    }

    headers["X-Correlation-Id"] = crypto.randomUUID();
    headers["X-Request-Id"] = crypto.randomUUID();

    try {
      const backendBaseUrl = config.baseUrlType === "auth" ? this.authBaseUrl : this.baseUrl;
      const requestUrl = this.buildUrl(config.url, config.baseUrlType);
      if (new URL(requestUrl).origin === new URL(backendBaseUrl).origin) {
        Object.assign(headers, posthogService.getRequestHeaders());
      }
    } catch {
      // Optional analytics metadata must not interrupt backend requests.
    }

    // Add a token for authenticated calls or an explicit public-request opt-in.
    if (config.requiresAuth !== false || config.includeAuthIfAvailable) {
      const token = authSnapshot.accessToken;

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }



    return headers;
  }

  /**
   * Build the full URL
   */
  private buildUrl(url: string, baseUrlType: "api" | "auth" = "api"): string {
    // If URL is already absolute, return as-is
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    // Choose the appropriate base URL
    const selectedBaseUrl = baseUrlType === "auth" ? this.authBaseUrl : this.baseUrl;

    // Remove leading slash if present to avoid double slashes
    const cleanUrl = url.startsWith("/") ? url.slice(1) : url;
    return `${selectedBaseUrl}/${cleanUrl}`;
  }

  /**
   * Create an HttpError from a fetch response
   */
  private async createHttpError(
    response: Response,
    lifecycle?: RequestLifecycleContext
  ): Promise<HttpError> {
    let errorData: any = {};

    try {
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const body = response.json();
        errorData = lifecycle
          ? await this.awaitRequestLifecycle(body, lifecycle)
          : await body;
      } else {
        const body = response.text();
        errorData = {
          message: lifecycle
            ? await this.awaitRequestLifecycle(body, lifecycle)
            : await body,
        };
      }
    } catch (e) {
      // If we can't parse the error response, use a generic message
      errorData = { message: `HTTP error! status: ${response.status}` };
    }

    if (lifecycle) this.assertRequestLifecycleCurrent(lifecycle);

    const error = new Error(
      errorData.error || errorData.message || `HTTP error! status: ${response.status}`
    ) as HttpError;
    error.status = response.status;
    error.response = {
      data: errorData,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    };

    return error;
  }

  /**
   * Attempt to refresh the token for the account context that sent the failed request.
   * Prevents multiple simultaneous refresh attempts.
   */
  private async attemptTokenRefresh(
    requestSnapshot: AuthTokenSnapshot
  ): Promise<TokenRefreshResult> {
    const currentSnapshot = this.getAuthTokenSnapshot();
    if (!this.authTokenSnapshotsMatch(requestSnapshot, currentSnapshot)) {
      const completedRefresh = this.getReusableCompletedRefresh(
        requestSnapshot,
        currentSnapshot
      );
      if (completedRefresh) return completedRefresh;
      return { outcome: "invalidated", source: requestSnapshot };
    }
    if (!this.authTokenPairValuesMatch(requestSnapshot, this.observedAuthTokenPair)) {
      return { outcome: "invalidated", source: requestSnapshot };
    }

    // If already refreshing, wait for that to complete
    if (this.isRefreshing && this.refreshPromise) {
      const sharedResult = await this.refreshPromise;
      return this.validateRefreshResultForRequest(sharedResult, requestSnapshot);
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doRefresh(requestSnapshot);

    try {
      const result = await this.refreshPromise;
      return this.validateRefreshResultForRequest(result, requestSnapshot);
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private validateRefreshResultForRequest(
    result: TokenRefreshResult,
    requestSnapshot: AuthTokenSnapshot
  ): TokenRefreshResult {
    if (!this.authTokenSnapshotsMatch(result.source, requestSnapshot)) {
      const completedRefresh = this.getReusableCompletedRefresh(
        requestSnapshot,
        this.getAuthTokenSnapshot()
      );
      if (completedRefresh) return completedRefresh;
      return { outcome: "invalidated", source: requestSnapshot };
    }
    if (
      result.outcome === "refreshed"
      && !this.authTokenSnapshotsMatch(result.installed, this.getAuthTokenSnapshot())
    ) {
      return { outcome: "invalidated", source: requestSnapshot };
    }
    return result;
  }

  private getReusableCompletedRefresh(
    requestSnapshot: AuthTokenSnapshot,
    currentSnapshot: AuthTokenSnapshot
  ): SuccessfulTokenRefresh | null {
    const completedRefresh = this.lastSuccessfulRefresh;
    if (
      completedRefresh
      && this.authTokenSnapshotsMatch(completedRefresh.source, requestSnapshot)
      && this.authTokenSnapshotsMatch(completedRefresh.installed, currentSnapshot)
    ) {
      return completedRefresh;
    }
    return null;
  }

  /**
   * Perform the actual token refresh using fetch directly to avoid recursion
   */
  private async doRefresh(source: AuthTokenSnapshot): Promise<TokenRefreshResult> {
    let refreshController: AbortController | null = null;
    try {
      if (!this.authTokenSnapshotsMatch(source, this.getAuthTokenSnapshot())) {
        return { outcome: "invalidated", source };
      }
      if (!this.authTokenPairValuesMatch(source, this.observedAuthTokenPair)) {
        return { outcome: "invalidated", source };
      }
      if (!source.accessToken || !source.refreshToken) {
        console.warn("No refresh token available for 401 recovery");
        return { outcome: "failed", source };
      }

      // Set up timeout to prevent hanging if auth service is unresponsive
      const controller = new AbortController();
      refreshController = controller;
      this.activeRefreshController = controller;
      const timeoutId = setTimeout(() => controller.abort(), this.defaultTimeout);

      // Backend expects refresh token in Authorization header (not access token)
      // The refresh token JWT has type: 'refresh' which backend validates
      let response: Response;
      try {
        response = await fetch(this.buildUrl("/refresh-token", "auth"), {
          method: "POST",
          headers: this.buildHeaders({
            method: "POST",
            url: "/refresh-token",
            baseUrlType: "auth",
            requiresAuth: false,
            headers: { Authorization: `Bearer ${source.refreshToken}` },
          }, source),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }

      if (!this.authTokenSnapshotsMatch(source, this.getAuthTokenSnapshot())) {
        return { outcome: "invalidated", source };
      }
      if (!response.ok) {
        console.error("Token refresh failed with status:", response.status);
        return { outcome: "failed", source };
      }

      const data = await response.json();
      const newAccessToken = data.accessToken || data.token;
      const newRefreshToken = data.refreshToken;

      if (newAccessToken) {
        // An account activation may have replaced the pair while this refresh
        // was in flight. Compare storage as well as the in-memory version so a
        // change made by another tab is caught even before its storage event.
        if (!this.authTokenSnapshotsMatch(source, this.getAuthTokenSnapshot())) {
          return { outcome: "invalidated", source };
        }
        const expectedRefreshToken = newRefreshToken || source.refreshToken;
        this.updateAuthTokens(newAccessToken, expectedRefreshToken);
        const installed = this.getAuthTokenSnapshot();
        if (
          installed.accessToken !== newAccessToken
          || installed.refreshToken !== expectedRefreshToken
        ) {
          this.lastSuccessfulRefresh = null;
          return { outcome: "invalidated", source };
        }
        console.log("Token refreshed successfully via 401 interceptor");
        const successfulRefresh: SuccessfulTokenRefresh = {
          outcome: "refreshed",
          source,
          installed,
        };
        this.lastSuccessfulRefresh = successfulRefresh;
        return successfulRefresh;
      }

      console.error("Token refresh response missing access token");
      return { outcome: "failed", source };
    } catch (error) {
      if (!this.authTokenSnapshotsMatch(source, this.getAuthTokenSnapshot())) {
        return { outcome: "invalidated", source };
      }
      if (error instanceof Error && error.name === "AbortError") {
        console.error("Token refresh timed out");
      } else {
        console.error("Token refresh failed in interceptor:", error);
      }
      return { outcome: "failed", source };
    } finally {
      if (this.activeRefreshController === refreshController) {
        this.activeRefreshController = null;
      }
    }
  }

  /**
   * Make an HTTP request with automatic 401 retry
   */
  public async request<T = any>(config: HttpRequestConfig): Promise<HttpResponse<T>> {
    const lifecycle = this.createRequestLifecycle(config);
    try {
      return await this.executeRequest<T>(config, lifecycle, config.retryOnUnauthorized !== false);
    } finally {
      this.releaseRequestLifecycle(lifecycle);
    }
  }

  /**
   * Internal request execution with optional 401 retry
   */
  private async executeRequest<T = any>(
    config: HttpRequestConfig,
    lifecycle: RequestLifecycleContext,
    allowRetryOn401: boolean,
    expectedAuthSnapshot?: AuthTokenSnapshot,
    staleRequestError?: HttpError
  ): Promise<HttpResponse<T>> {
    this.assertRequestLifecycleCurrent(lifecycle);
    const authSnapshot = this.getAuthTokenSnapshot();
    if (
      expectedAuthSnapshot
      && !this.authTokenSnapshotsMatch(expectedAuthSnapshot, authSnapshot)
    ) {
      throw staleRequestError || new Error("Request invalidated by account context change");
    }

    let abortState: RequestAbortState | undefined;
    try {
      const url = this.buildUrl(config.url, config.baseUrlType || "api");
      const headers = this.buildHeaders(config, authSnapshot);

      const requestInit: RequestInit = {
        method: config.method,
        headers,
      };
      if (config.credentials !== undefined) requestInit.credentials = config.credentials;
      if (config.keepalive !== undefined) requestInit.keepalive = config.keepalive;
      if (config.cache !== undefined) requestInit.cache = config.cache;

      // Add body for methods that support it
      if (config.data && ["POST", "PUT", "PATCH", "DELETE"].includes(config.method)) {
        if (config.data instanceof FormData || config.data instanceof Blob) {
          // FormData and Blob/File objects should be sent as-is
          requestInit.body = config.data;
        } else {
          requestInit.body = JSON.stringify(config.data);
        }
      }

      abortState = this.createRequestAbortState(
        lifecycle,
        config.signal,
        config.timeout || this.defaultTimeout
      );
      requestInit.signal = abortState.signal;

      const response = await this.awaitRequestLifecycle(
        fetch(url, requestInit),
        lifecycle
      );
      abortState.clearTimeout();
      this.assertRequestLifecycleCurrent(lifecycle);

      if (!response.ok) {
        // Handle 401 with automatic token refresh and retry
        if (
          response.status === 401 &&
          allowRetryOn401 &&
          config.requiresAuth !== false
        ) {
          console.log("Received 401, attempting token refresh...");
          const unauthorizedError = await this.createHttpError(response, lifecycle);
          this.assertRequestLifecycleCurrent(lifecycle);
          const refreshResult = await this.awaitRequestLifecycle(
            this.attemptTokenRefresh(authSnapshot),
            lifecycle
          );
          this.assertRequestLifecycleCurrent(lifecycle);
          if (refreshResult.outcome === "refreshed") {
            console.log("Token refreshed, retrying original request...");
            // Retry without allowing another 401 retry to prevent infinite loops
            abortState.cleanup();
            abortState = undefined;
            return this.executeRequest<T>(
              config,
              lifecycle,
              false,
              refreshResult.installed,
              unauthorizedError
            );
          }
          if (refreshResult.outcome === "invalidated") {
            console.warn("Token refresh invalidated by account context change");
          } else {
            console.warn("Token refresh failed, propagating 401 error");
          }
          throw unauthorizedError;
        }
        const httpError = await this.createHttpError(response, lifecycle);
        this.assertRequestLifecycleCurrent(lifecycle);
        this.handleAccountContextAccessDenied(httpError, config, lifecycle);
        throw httpError;
      }

      // Parse response data.
      // HEAD responses carry no body (Express auto-handles HEAD against the GET
      // route and strips the body). Parsing would throw on the empty payload.
      let data: T;
      if (config.method === "HEAD") {
        data = undefined as T;
      } else {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          data = await this.awaitRequestLifecycle(response.json(), lifecycle);
        } else {
          data = (await this.awaitRequestLifecycle(response.text(), lifecycle)) as any;
        }
      }
      this.assertRequestLifecycleCurrent(lifecycle);

      return {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      };
    } catch (error) {
      if (error instanceof AccountContextRequestInvalidatedError) throw error;
      this.assertRequestLifecycleCurrent(lifecycle);

      // Handle timeout and network errors
      if (error instanceof Error && error.name === "AbortError") {
        const timeoutError = new Error("Request timeout") as HttpError;
        timeoutError.status = 408;
        throw timeoutError;
      }

      // Re-throw HttpError as-is
      if ((error as HttpError).response) {
        throw error;
      }

      // Wrap other errors
      const wrappedError = new Error(
        `Network error: ${error instanceof Error ? error.message : "Unknown error"}`
      ) as HttpError;
      throw wrappedError;
    } finally {
      abortState?.cleanup();
    }
  }

  /**
   * Convenience method for GET requests
   */
  public async get<T = any>(url: string, config?: Partial<HttpRequestConfig>): Promise<HttpResponse<T>> {
    return this.request<T>({
      method: "GET",
      url,
      ...config,
    });
  }

  /**
   * Convenience method for HEAD requests (existence checks, no body)
   */
  public async head(url: string, config?: Partial<HttpRequestConfig>): Promise<HttpResponse<void>> {
    return this.request<void>({
      method: "HEAD",
      url,
      ...config,
    });
  }

  /**
   * Convenience method for POST requests
   */
  public async post<T = any>(url: string, data?: any, config?: Partial<HttpRequestConfig>): Promise<HttpResponse<T>> {
    return this.request<T>({
      method: "POST",
      url,
      data,
      ...config,
    });
  }

  /**
   * Convenience method for PUT requests
   */
  public async put<T = any>(url: string, data?: any, config?: Partial<HttpRequestConfig>): Promise<HttpResponse<T>> {
    return this.request<T>({
      method: "PUT",
      url,
      data,
      ...config,
    });
  }

  /**
   * Convenience method for DELETE requests
   */
  public async delete<T = any>(url: string, config?: Partial<HttpRequestConfig>): Promise<HttpResponse<T>> {
    return this.request<T>({
      method: "DELETE",
      url,
      ...config,
    });
  }

  /**
   * Convenience method for PATCH requests
   */
  public async patch<T = any>(url: string, data?: any, config?: Partial<HttpRequestConfig>): Promise<HttpResponse<T>> {
    return this.request<T>({
      method: "PATCH",
      url,
      data,
      ...config,
    });
  }

  // Auth API convenience methods

  /**
   * Convenience method for GET requests to auth API
   */
  public async getAuth<T = any>(url: string, config?: Partial<HttpRequestConfig>): Promise<HttpResponse<T>> {
    return this.request<T>({
      method: "GET",
      url,
      baseUrlType: "auth",
      ...config,
    });
  }

  /**
   * Convenience method for POST requests to auth API
   */
  public async postAuth<T = any>(
    url: string,
    data?: any,
    config?: Partial<HttpRequestConfig>
  ): Promise<HttpResponse<T>> {
    return this.request<T>({
      method: "POST",
      url,
      data,
      baseUrlType: "auth",
      ...config,
    });
  }

  /**
   * Convenience method for PUT requests to auth API
   */
  public async putAuth<T = any>(
    url: string,
    data?: any,
    config?: Partial<HttpRequestConfig>
  ): Promise<HttpResponse<T>> {
    return this.request<T>({
      method: "PUT",
      url,
      data,
      baseUrlType: "auth",
      ...config,
    });
  }

  /**
   * Convenience method for DELETE requests to auth API
   */
  public async deleteAuth<T = any>(url: string, config?: Partial<HttpRequestConfig>): Promise<HttpResponse<T>> {
    return this.request<T>({
      method: "DELETE",
      url,
      baseUrlType: "auth",
      ...config,
    });
  }

  /**
   * Convenience method for PATCH requests to auth API
   */
  public async patchAuth<T = any>(
    url: string,
    data?: any,
    config?: Partial<HttpRequestConfig>
  ): Promise<HttpResponse<T>> {
    return this.request<T>({
      method: "PATCH",
      url,
      data,
      baseUrlType: "auth",
      ...config,
    });
  }

  /**
   * Refresh token method - uses refresh token for authentication
   * Backend expects refresh token in Authorization header (not access token)
   */
  public async refreshToken<T = any>(
    config?: Pick<HttpRequestConfig, "requestScope">
  ): Promise<HttpResponse<T>> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error("Refresh token not found");
    }

    return this.request<T>({
      method: "POST",
      url: "/refresh-token",
      baseUrlType: "auth",
      headers: {
        "Authorization": `Bearer ${refreshToken}`,
      },
      requiresAuth: false, // We're manually setting the refresh token as Authorization
      requestScope: config?.requestScope ?? "actor",
    });
  }

  /**
   * Set the authentication token key (if different from default)
   */
  public setAuthTokenKey(key: string): void {
    this.authTokenKey = key;
    this.adoptPersistedAuthTokenPair();
  }

  /**
   * Set the default timeout for requests
   */
  public setDefaultTimeout(timeout: number): void {
    this.defaultTimeout = timeout;
  }

  /**
   * Get the current base URL
   */
  public getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Get the current auth base URL
   */
  public getAuthBaseUrl(): string {
    return this.authBaseUrl;
  }

  /**
   * Update the base URL (useful for testing or environment changes)
   */
  public setBaseUrl(baseUrl: string): void {
    this.baseUrl = baseUrl;
  }

  /**
   * Update the auth base URL (useful for testing or environment changes)
   */
  public setAuthBaseUrl(authBaseUrl: string): void {
    this.authBaseUrl = authBaseUrl;
  }

  /**
   * Download a file as a Blob with proper authentication and headers.
   * Uses the same auth and header logic as other requests.
   * 
   * @param url - The URL to download from (relative or absolute)
   * @param config - Optional request configuration
   * @returns Promise<Blob> - The downloaded file as a Blob with correct MIME type
   */
  public async downloadBlob(url: string, config?: Partial<HttpRequestConfig>): Promise<Blob> {
    const requestConfig: HttpRequestConfig = {
      ...config,
      method: "GET",
      url,
    };
    const lifecycle = this.createRequestLifecycle(requestConfig);
    const buildHeadersForDownload = (authSnapshot: AuthTokenSnapshot) => {
      const headers = this.buildHeaders(requestConfig, authSnapshot);
      delete headers["Content-Type"];
      return headers;
    };

    let authSnapshotAtLastAttempt = this.getAuthTokenSnapshot();
    const execute = async (
      expectedAuthSnapshot?: AuthTokenSnapshot,
      staleRequestError?: HttpError
    ): Promise<Blob> => {
      this.assertRequestLifecycleCurrent(lifecycle);
      const authSnapshot = this.getAuthTokenSnapshot();
      if (
        expectedAuthSnapshot
        && !this.authTokenSnapshotsMatch(expectedAuthSnapshot, authSnapshot)
      ) {
        throw staleRequestError || new Error("Request invalidated by account context change");
      }
      authSnapshotAtLastAttempt = authSnapshot;
      const fullUrl = this.buildUrl(url, requestConfig.baseUrlType || "api");
      const headers = buildHeadersForDownload(authSnapshot);
      const abortState = this.createRequestAbortState(
        lifecycle,
        requestConfig.signal,
        requestConfig.timeout || this.defaultTimeout
      );

      try {
        const response = await this.awaitRequestLifecycle(
          fetch(fullUrl, {
            method: "GET",
            headers,
            signal: abortState.signal,
          }),
          lifecycle
        );

        abortState.clearTimeout();
        this.assertRequestLifecycleCurrent(lifecycle);

        if (!response.ok) {
          throw await this.createHttpError(response, lifecycle);
        }

        const contentType = response.headers.get("Content-Type") || "application/octet-stream";
        const arrayBuffer = await this.awaitRequestLifecycle(
          response.arrayBuffer(),
          lifecycle
        );
        this.assertRequestLifecycleCurrent(lifecycle);

        return new Blob([arrayBuffer], { type: contentType });
      } catch (error) {
        if (error instanceof AccountContextRequestInvalidatedError) throw error;
        this.assertRequestLifecycleCurrent(lifecycle);
        throw error;
      } finally {
        abortState.cleanup();
      }
    };

    try {
      try {
        return await execute();
      } catch (error) {
        if (error instanceof AccountContextRequestInvalidatedError) throw error;
        this.assertRequestLifecycleCurrent(lifecycle);

        // Handle timeout explicitly
        if (error instanceof Error && error.name === "AbortError") {
          const timeoutError = new Error("Request timeout") as HttpError;
          timeoutError.status = 408;
          throw timeoutError;
        }

        // Retry once on 401 using the same logic as other requests
        if (
          (error as HttpError).status === 401 &&
          requestConfig.requiresAuth !== false
        ) {
          const refreshResult = await this.awaitRequestLifecycle(
            this.attemptTokenRefresh(authSnapshotAtLastAttempt),
            lifecycle
          );
          this.assertRequestLifecycleCurrent(lifecycle);
          if (refreshResult.outcome === "refreshed") {
            return execute(refreshResult.installed, error as HttpError);
          }
        }

        if ((error as HttpError).response) {
          throw error;
        }

        const wrappedError = new Error(
          `Download failed: ${error instanceof Error ? error.message : "Unknown error"}`
        ) as HttpError;
        throw wrappedError;
      }
    } finally {
      this.releaseRequestLifecycle(lifecycle);
    }
  }

  /**
   * Download a file by reading the response body as a stream, reporting progress as chunks
   * arrive. Use this instead of {@link downloadBlob} for large responses where the caller
   * needs live feedback or a working cancel.
   *
   * Two behaviours differ from every other method here, both deliberate:
   *
   * - **The timeout covers the connection only, not the transfer.** It is cleared the moment
   *   response headers arrive. A multi-minute stream is not a hung request, and applying the
   *   normal request timeout to it would abort healthy downloads.
   * - **A mid-stream failure cannot arrive as an HTTP status.** The 200 and some bytes are
   *   already on the wire, so a server-side failure surfaces as the reader rejecting (typically
   *   `TypeError: network error`). Callers must treat that as a total failure and discard what
   *   they received -- a truncated file that looks whole is worse than no file.
   *
   * The 401 retry applies to the connect stage only. A 401 is raised before `getReader()` is
   * ever called, so nothing has been consumed and replaying the request cannot splice two
   * responses together. Once the body is being read there is no retry of any kind -- a
   * mid-stream failure is terminal.
   */
  public async streamDownload(
    url: string,
    options: {
      signal?: AbortSignal;
      /** Called for every chunk. Throttle in the UI layer; this reports raw progress. */
      onProgress?: (progress: { rowCount: number; byteCount: number }) => void;
      requiresAuth?: boolean;
      baseUrlType?: "api" | "auth";
      headers?: Record<string, string>;
      requestScope?: HttpRequestScope;
      /** Applies to establishing the connection only, never to reading the body. */
      connectTimeout?: number;
    } = {}
  ): Promise<{ blob: Blob; rowCount: number; byteCount: number }> {
    const requestConfig: HttpRequestConfig = {
      method: "GET",
      url,
      requiresAuth: options.requiresAuth,
      baseUrlType: options.baseUrlType,
      headers: options.headers,
      requestScope: options.requestScope,
    };
    const lifecycle = this.createRequestLifecycle(requestConfig);
    const fullUrl = this.buildUrl(url, requestConfig.baseUrlType || "api");

    // Rebuilt per attempt so a retry after a token refresh picks up the new access token
    // rather than replaying the expired one.
    const buildStreamHeaders = (
      authSnapshot: AuthTokenSnapshot
    ): Record<string, string> => {
      const headers = this.buildHeaders(requestConfig, authSnapshot);
      delete headers["Content-Type"];
      return headers;
    };

    let authSnapshotAtLastConnect = this.getAuthTokenSnapshot();

    /**
     * Establish the connection and check the status. Deliberately stops short of touching
     * `response.body`, so any failure here is safe to retry: not a byte has been consumed.
     * Returns a detach for the caller's abort listener, which must stay attached for the whole
     * read so a cancel still works once streaming has started.
     */
    const connect = async (
      expectedAuthSnapshot?: AuthTokenSnapshot,
      staleRequestError?: HttpError
    ): Promise<{ response: Response; detach: () => void }> => {
      this.assertRequestLifecycleCurrent(lifecycle);
      const authSnapshot = this.getAuthTokenSnapshot();
      if (
        expectedAuthSnapshot
        && !this.authTokenSnapshotsMatch(expectedAuthSnapshot, authSnapshot)
      ) {
        throw staleRequestError || new Error("Request invalidated by account context change");
      }
      authSnapshotAtLastConnect = authSnapshot;

      const abortState = this.createRequestAbortState(
        lifecycle,
        options.signal,
        options.connectTimeout || this.defaultTimeout
      );

      try {
        const response = await this.awaitRequestLifecycle(
          fetch(fullUrl, {
            method: "GET",
            headers: buildStreamHeaders(authSnapshot),
            signal: abortState.signal,
          }),
          lifecycle
        );

        // Headers are in: the request connected, so the transfer is no longer on a clock.
        abortState.clearTimeout();
        this.assertRequestLifecycleCurrent(lifecycle);

        if (!response.ok) {
          throw await this.createHttpError(response, lifecycle);
        }
        if (!response.body) {
          throw new Error("Response body is not readable");
        }

        return { response, detach: abortState.cleanup };
      } catch (error) {
        abortState.cleanup();
        if (error instanceof AccountContextRequestInvalidatedError) throw error;
        this.assertRequestLifecycleCurrent(lifecycle);
        throw error;
      }
    };

    let connection: { response: Response; detach: () => void } | undefined;

    try {
      try {
        connection = await connect();
      } catch (error) {
        if (error instanceof AccountContextRequestInvalidatedError) throw error;
        this.assertRequestLifecycleCurrent(lifecycle);
        // Retry once on an expired access token. This is the connect stage, so no chunk has been
        // read and the replay produces one clean response. Without it the caller is stuck: the
        // 401 surfaces as a download failure and every retry resends the same expired token
        // until some other request happens to refresh it.
        if ((error as HttpError).status === 401 && options.requiresAuth !== false) {
          const refreshResult = await this.awaitRequestLifecycle(
            this.attemptTokenRefresh(authSnapshotAtLastConnect),
            lifecycle
          );
          this.assertRequestLifecycleCurrent(lifecycle);
          if (refreshResult.outcome !== "refreshed") throw error;
          connection = await connect(refreshResult.installed, error as HttpError);
        } else {
          throw error;
        }
      }

      const { response } = connection;
      const contentType = response.headers.get("Content-Type") || "application/octet-stream";
      // From here on nothing is retried: chunks are being consumed, so a second request could
      // only ever be spliced onto a partial one.
      const reader = response.body!.getReader();
      const chunks: Uint8Array[] = [];
      let rowCount = 0;
      let byteCount = 0;

      for (;;) {
        const { done, value } = await this.awaitRequestLifecycle(
          reader.read(),
          lifecycle
        );
        this.assertRequestLifecycleCurrent(lifecycle);
        if (done) break;
        if (!value) continue;

        chunks.push(value);
        byteCount += value.length;
        // Counting the 0x0A byte rather than decoding to text: no UTF-8 continuation byte can
        // ever be 0x0A, so a raw byte scan is exact and does not risk splitting a multi-byte
        // character across a chunk boundary.
        for (let i = 0; i < value.length; i++) {
          if (value[i] === 0x0a) rowCount++;
        }

        options.onProgress?.({ rowCount, byteCount });
      }

      this.assertRequestLifecycleCurrent(lifecycle);
      return { blob: new Blob(chunks as BlobPart[], { type: contentType }), rowCount, byteCount };
    } catch (error) {
      if (error instanceof AccountContextRequestInvalidatedError) throw error;
      this.assertRequestLifecycleCurrent(lifecycle);

      // Nothing partial escapes: `chunks` is local, so an error here discards everything read.
      if (error instanceof Error && error.name === "AbortError") {
        // A caller cancel and a connect timeout both land here. Only the timeout is a fault.
        if (options.signal?.aborted) throw error;
        const timeoutError = new Error("Request timeout") as HttpError;
        timeoutError.status = 408;
        throw timeoutError;
      }

      if ((error as HttpError).response) {
        throw error;
      }

      throw new Error(
        `Download failed: ${error instanceof Error ? error.message : "Unknown error"}`
      ) as HttpError;
    } finally {
      connection?.detach();
      this.releaseRequestLifecycle(lifecycle);
    }
  }
  /**
   * Opens an authenticated server-sent-events stream (PWA-2689) and hands the body
   * reader back to the caller. This is the transport only: the caller owns reading,
   * framing, reconnecting and the abort signal.
   *
   * Mirrors `streamDownload`'s connect stage. The 401 refresh-and-replay applies only
   * before `getReader()` is called; once the reader is returned nothing is retried.
   * With `requestScope: "actor"` the connection is registered in the actor request
   * lifecycle, so an auth session transition (logout) aborts it. The lifecycle stays
   * registered until the caller invokes `detach()`, which must happen exactly once
   * after the stream ends for any reason.
   */
  public async openEventStream(
    url: string,
    options: {
      /** Caller-owned; aborting closes the connection at any stage. */
      signal: AbortSignal;
      requestScope?: HttpRequestScope;
      baseUrlType?: "api" | "auth";
      /** Applies to establishing the connection only, never to reading the body. */
      connectTimeout?: number;
    }
  ): Promise<EventStreamConnection> {
    const requestConfig: HttpRequestConfig = {
      method: "GET",
      url,
      baseUrlType: options.baseUrlType,
      requestScope: options.requestScope,
    };
    const lifecycle = this.createRequestLifecycle(requestConfig);
    const fullUrl = this.buildUrl(url, requestConfig.baseUrlType || "api");

    const buildStreamHeaders = (
      authSnapshot: AuthTokenSnapshot
    ): Record<string, string> => {
      const headers = this.buildHeaders(requestConfig, authSnapshot);
      delete headers["Content-Type"];
      headers["Accept"] = "text/event-stream";
      return headers;
    };

    let authSnapshotAtLastConnect = this.getAuthTokenSnapshot();

    const connect = async (
      expectedAuthSnapshot?: AuthTokenSnapshot,
      staleRequestError?: HttpError
    ): Promise<{ response: Response; detach: () => void }> => {
      this.assertRequestLifecycleCurrent(lifecycle);
      const authSnapshot = this.getAuthTokenSnapshot();
      if (
        expectedAuthSnapshot
        && !this.authTokenSnapshotsMatch(expectedAuthSnapshot, authSnapshot)
      ) {
        throw staleRequestError || new Error("Request invalidated by account context change");
      }
      authSnapshotAtLastConnect = authSnapshot;

      const abortState = this.createRequestAbortState(
        lifecycle,
        options.signal,
        options.connectTimeout || this.defaultTimeout
      );

      try {
        const response = await this.awaitRequestLifecycle(
          fetch(fullUrl, {
            method: "GET",
            headers: buildStreamHeaders(authSnapshot),
            signal: abortState.signal,
          }),
          lifecycle
        );

        // Headers are in: the connection is established and no longer on a clock.
        abortState.clearTimeout();
        this.assertRequestLifecycleCurrent(lifecycle);

        if (!response.ok) {
          throw await this.createHttpError(response, lifecycle);
        }
        if (!response.body) {
          throw new Error("Response body is not readable");
        }

        return { response, detach: abortState.cleanup };
      } catch (error) {
        abortState.cleanup();
        if (error instanceof AccountContextRequestInvalidatedError) throw error;
        this.assertRequestLifecycleCurrent(lifecycle);
        throw error;
      }
    };

    try {
      let connection: { response: Response; detach: () => void };
      try {
        connection = await connect();
      } catch (error) {
        if (error instanceof AccountContextRequestInvalidatedError) throw error;
        this.assertRequestLifecycleCurrent(lifecycle);
        if ((error as HttpError).status === 401) {
          const refreshResult = await this.awaitRequestLifecycle(
            this.attemptTokenRefresh(authSnapshotAtLastConnect),
            lifecycle
          );
          this.assertRequestLifecycleCurrent(lifecycle);
          if (refreshResult.outcome !== "refreshed") throw error;
          connection = await connect(refreshResult.installed, error as HttpError);
        } else {
          throw error;
        }
      }

      const { response, detach } = connection;
      // From here on nothing is retried: the caller consumes the body.
      const reader = response.body!.getReader();
      let released = false;
      return {
        response,
        reader,
        detach: () => {
          if (released) return;
          released = true;
          detach();
          this.releaseRequestLifecycle(lifecycle);
        },
      };
    } catch (error) {
      this.releaseRequestLifecycle(lifecycle);
      if (error instanceof AccountContextRequestInvalidatedError) throw error;
      if (error instanceof Error && error.name === "AbortError") {
        // A caller cancel and a connect timeout both land here. Only the timeout is a fault.
        if (options.signal.aborted) throw error;
        const timeoutError = new Error("Request timeout") as HttpError;
        timeoutError.status = 408;
        throw timeoutError;
      }
      throw error;
    }
  }
}

// Export singleton instance
export const httpService = HttpService.getInstance();
export default httpService;
